import { icon as iconMacro } from '@fortawesome/fontawesome-svg-core/import.macro';
import { SxProps } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListParams } from 'src/utils/useListSearchParams';

import { Icon, IconDefinition } from './Icon';
import { IconButton } from './IconButton';
import { InputAdornment, TextField } from './TextField';
interface SearchParamSearchTextFieldProps {
  params: ListParams;
  updateParam: (k: string, v: any) => void;
  debounceDuration?: number;
  sx?: SxProps;
  placeholder?: string;
  endAdornment?: React.ReactNode;
}

export const SearchParamSearchTextField: React.FC<
  SearchParamSearchTextFieldProps
> = ({
  params,
  updateParam,
  debounceDuration = 400,
  sx,
  placeholder,
  endAdornment,
}) => {
  const [internalValue, setInternalValue] = useState(params.search);

  const debouncedSetSearchParam = useMemo(() => {
    return debounce((newVal) => {
      updateParam('search', newVal);
    }, debounceDuration);
  }, [debounceDuration, updateParam]);

  const onChange = useCallback(
    (newValue) => {
      setInternalValue(newValue);
      debouncedSetSearchParam(newValue);
    },
    [debouncedSetSearchParam]
  );

  useEffect(() => {
    setInternalValue(params.search);
  }, [params.search]);

  const onCancel = () => {
    setInternalValue('');
    updateParam('search', '');
  };

  return (
    <SearchTextField
      value={internalValue}
      onSearch={onChange}
      onCancel={onCancel}
      placeholder={placeholder}
      endAdornment={endAdornment}
      sx={sx}
    />
  );
};

interface SearchTextFieldProps {
  value: string;
  onCancel: () => void;
  onSearch: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  icon?: IconDefinition;
  showClearButton?: boolean;
  autoFocus?: boolean;
  sx?: SxProps;
  endAdornment?: React.ReactNode;
}
export const SearchTextField: React.FC<SearchTextFieldProps> = ({
  value,
  onCancel,
  onSearch,
  onFocus,
  onBlur,
  showClearButton,
  icon = iconMacro({ name: 'search', style: 'regular' }),
  placeholder = 'Search',
  autoFocus,
  sx,
  endAdornment,
}) => {
  return (
    <TextField
      sx={[
        { '& .MuiInputBase-root': { pr: 0 } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      placeholder={placeholder}
      startAdornment={
        <InputAdornment position="start">
          <Icon color="action" size="medium" icon={icon} />
        </InputAdornment>
      }
      endAdornment={
        (value || showClearButton || endAdornment) && (
          <InputAdornment position="end">
            {endAdornment}
            {(value || showClearButton) && (
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onCancel();
                }}
              >
                <Icon
                  color="secondary"
                  size="medium"
                  icon={iconMacro({ name: 'times-circle', style: 'regular' })}
                />
              </IconButton>
            )}
          </InputAdornment>
        )
      }
      value={value}
      onChange={(event) => onSearch(event.target.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
    />
  );
};
